import api from "../api/httpClient";
import { API_PATHS } from "../api/apiPaths";
import { getCachedOrFetch, getTenantCacheKey, invalidateCache } from "../utils/requestCache";

const STUDENT_PROFILE_CACHE_KEY = "studentProfileCache";
const STUDENT_LIST_CACHE_KEY = "students:list";
let cachedProfile = null;
let profileRequestPromise = null;

const readCachedProfile = () => {
  try {
    const stored = localStorage.getItem(STUDENT_PROFILE_CACHE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

const writeCachedProfile = (profile) => {
  try {
    localStorage.setItem(STUDENT_PROFILE_CACHE_KEY, JSON.stringify(profile));
    cachedProfile = profile;
  } catch {
    // Ignore storage write failures.
  }
};

const extractPayload = (responseData) => responseData?.data ?? responseData ?? {};

const extractStudentList = (responseData) => {
  const payload = extractPayload(responseData);
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.students)) return payload.students;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

class StudentService {
  getCachedProfile() {
    if (cachedProfile) return cachedProfile;

    cachedProfile = readCachedProfile();
    return cachedProfile;
  }

  getCachedSchoolName() {
    return this.getCachedProfile()?.schoolName || "";
  }

  clearCachedProfile() {
    cachedProfile = null;
    profileRequestPromise = null;
    localStorage.removeItem(STUDENT_PROFILE_CACHE_KEY);
  }

  invalidateStudentsCache() {
    invalidateCache(getTenantCacheKey(STUDENT_LIST_CACHE_KEY));
  }

  async getProfile(options = {}) {
    const { forceRefresh = false, preferCache = false } = options;

    if (!forceRefresh && preferCache) {
      const cached = this.getCachedProfile();
      if (cached) {
        return cached;
      }
    }

    if (!forceRefresh && profileRequestPromise) {
      return profileRequestPromise;
    }

    profileRequestPromise = api.get(API_PATHS.student.profile)
      .then((response) => {
        const apiResponse = response?.data || {};

        if (apiResponse.success === false) {
          throw new Error(apiResponse.message || "Unable to fetch student profile.");
        }

        const profile = apiResponse.data || {};
        writeCachedProfile(profile);
        return profile;
      })
      .finally(() => {
        profileRequestPromise = null;
      });

    return profileRequestPromise;
  }

  async createStudent(studentPayload = {}) {
    const response = await api.post(API_PATHS.student.createStudent(), studentPayload);
    const apiResponse = response?.data || {};

    if (apiResponse.success === false) {
      throw new Error(apiResponse.message || "Unable to create student.");
    }

    this.invalidateStudentsCache();

    return extractPayload(apiResponse);
  }

  async importStudents(file, onProgress) {
    const formData = new FormData();
    formData.append("file", file);
    const response = await api.post(API_PATHS.student.importStudents(), formData, {
      onUploadProgress: (event) => {
        if (!event.total || typeof onProgress !== "function") return;
        onProgress(Math.min(100, Math.round((event.loaded * 100) / event.total)));
      },
    });
    const apiResponse = response?.data || {};

    if (apiResponse.success === false) {
      throw new Error(apiResponse.message || "Unable to import student records.");
    }

    this.invalidateStudentsCache();
    return extractPayload(apiResponse);
  }

  async getStudents(options = {}) {
    const { forceRefresh = false } = options;
    const response = await getCachedOrFetch(
      getTenantCacheKey(STUDENT_LIST_CACHE_KEY),
      () => api.get(API_PATHS.student.viewAllStudents()),
      { ttlMs: 120000, forceRefresh }
    );
    const apiResponse = response?.data || {};

    if (apiResponse.success === false) {
      throw new Error(apiResponse.message || "Unable to fetch students.");
    }

    return extractStudentList(apiResponse);
  }
}

export default new StudentService();
